import type {
  IAllWinnerCategories,
  IBigWinner,
  IHotGame,
} from '~/interfaces/winners/winnersInterfaces';
import type { IGameCategory } from '~/interfaces/dto/games/response';
import { useServices } from '~/composables/useServices';
import type {
  IGameWinRecord,
  TMyBigWinsSortField,
  TMyBigWinsSortOrder,
} from '~/interfaces/winners/my-big-wins';

export interface WinnersState extends IAllWinnerCategories {
  winnersLoading: boolean;
  bigWinsLoading: boolean;
  totalBigWins: number;
  bigWinsSortField: TMyBigWinsSortField;
  bigWinsSortOrder: TMyBigWinsSortOrder;
  bigWinsFilterBy: string;
  bigWinsFilterOptions: string[];
  error: string | null;
}

const DEFAULTS: WinnersState = {
  myBigWins: [] as IGameWinRecord[],
  totalBigWins: 0 as number,
  bigWinsSortField: 'WinAmount' as TMyBigWinsSortField,
  bigWinsSortOrder: 'Desc' as TMyBigWinsSortOrder,
  bigWinsFilterBy: '' as string,
  bigWinsFilterOptions: [] as string[],
  hotGames: [] as IHotGame[],
  bigWinners: [] as IBigWinner[],
  currentWinners: [] as IBigWinner[],
  mostPopularGames: [] as Partial<IGameCategory>[],
  mostLikedGames: [] as Partial<IGameCategory>[],
  winnersLoading: false,
  bigWinsLoading: false,
  error: null,
};

const state = ref<WinnersState>(DEFAULTS);

export const useWinnersData = () => {
  const { $winnerService } = useServices();
  const { $t } = useNuxtApp();

  const set = (data: Partial<IAllWinnerCategories>) => {
    const filterResponse = <T>(arr: T[], keyFn: (x: T) => string | number) => {
      const seen = new Set<string | number>();
      const out: T[] = [];
      for (const item of arr) {
        const k = keyFn(item);
        if (!seen.has(k)) {
          seen.add(k);
          out.push(item);
        }
      }
      return out;
    };

    for (const key in data) {
      if (!(key in state.value)) continue;
      const toFilter = data[key];
      if (!Array.isArray(toFilter)) continue;

      let deduped = toFilter;

      switch (key as keyof IAllWinnerCategories) {
        case 'myBigWins':
          deduped = filterResponse(toFilter, (x) => x.transactionId);
          break;
        case 'hotGames':
          deduped = filterResponse(
            toFilter,
            (x) => x?.transactionTypeId || x.game?.id,
          );
          break;
        case 'mostPopularGames':
        case 'mostLikedGames':
          deduped = filterResponse(toFilter, (x) => x.id || x.alias);
          break;
      }
      state.value[key] = [...deduped];
    }
    state.value.error = null;
  };
  const loadBigWinners = async () => {
    if (state.value.winnersLoading) return;
    state.value.winnersLoading = true;
    state.value.error = null;
    try {
      const winnersResponse = await $winnerService.fetchAllWinners();
      if (!!winnersResponse) set(winnersResponse);
    } catch (err) {
      state.value.error = err?.message ?? 'winners-general-error';
    } finally {
      state.value.winnersLoading = false;
    }
  };
  const loadMyBigWins = async (
    options: {
      sortOrder?: 'Asc' | 'Desc';
      sortField?: 'WinAmount' | 'WagerAmount' | 'WagerDate';
      skip: number;
      limit: number;
      filter?: string;
      resetGameNames?: boolean;
    } = { skip: 0, limit: 8, resetGameNames: true },
  ) => {
    if (state.value.bigWinsLoading) return;
    if (!options.sortOrder) options.sortOrder = state.value.bigWinsSortOrder;
    if (!options.sortField) options.sortField = state.value.bigWinsSortField;
    state.value.bigWinsLoading = true;
    state.value.error = null;
    try {
      const myBigWinsResponse = await $winnerService.fetchMyBigWins(options);
      if (!!myBigWinsResponse.response) {
        set({ myBigWins: [...myBigWinsResponse.response] });
        state.value.totalBigWins = myBigWinsResponse.total || 0;
        if (options.resetGameNames)
          state.value.bigWinsFilterOptions = [
            $t('see-all'),
            ...myBigWinsResponse.filters,
          ];
      }
    } catch (err) {
      state.value.error = err?.message ?? 'winners-general-error';
    } finally {
      state.value.bigWinsLoading = false;
    }
  };
  return {
    loadBigWinners,
    loadMyBigWins,
    set,
    myBigWins: computed(() => state.value.myBigWins) as Readonly<
      Ref<IGameWinRecord[]>
    >,
    totalBigWins: computed(() => state.value.totalBigWins) as Readonly<
      Ref<number>
    >,
    bigWinsSortField: computed({
      get: () => state.value.bigWinsSortField,
      set: (value: TMyBigWinsSortField) =>
        (state.value.bigWinsSortField = value),
    }),
    bigWinsSortOrder: computed({
      get: () => state.value.bigWinsSortOrder,
      set: (value: TMyBigWinsSortOrder) =>
        (state.value.bigWinsSortOrder = value),
    }),
    bigWinsFilterBy: computed({
      get: () => state.value.bigWinsFilterBy,
      set: (value: string) => (state.value.bigWinsFilterBy = value),
    }),
    bigWinsFilterOptions: computed(
      () => state.value.bigWinsFilterOptions,
    ) as Readonly<Ref<string[]>>,
    hotGames: computed(() => state.value.hotGames) as Readonly<Ref<IHotGame[]>>,
    bigWinners: computed(() => state.value.bigWinners) as Readonly<
      Ref<IBigWinner[]>
    >,
    currentWinners: computed(() =>
      state.value.currentWinners.filter(
        (w) => Number(w.wagerAmount) < Number(w.payoutAmount),
      ),
    ) as Readonly<Ref<IBigWinner[]>>,
    mostPopularGames: computed(() => state.value.mostPopularGames) as Readonly<
      Ref<Partial<IGameCategory>[]>
    >,
    mostLikedGames: computed(() => state.value.mostLikedGames) as Readonly<
      Ref<Partial<IGameCategory>[]>
    >,
    winnersLoading: computed(() => state.value.winnersLoading) as Readonly<
      Ref<boolean>
    >,
    bigWinsLoading: computed(() => state.value.bigWinsLoading) as Readonly<
      Ref<boolean>
    >,
    error: computed(() => state.value.error) as Readonly<Ref<string | null>>,
  } as const;
};
